import React from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { useClient } from '@hooks/useClient'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { CampaignTypeEnum } from '@interfaces/promotionCampaign/CampaignTypeEnum'
import { CampaignStatusEnum } from '@interfaces/promotionCampaign/CampaignStatusEnum'
import { CampaignFormType } from '@models/promotionCampaign/CampaignFormType'
import { EditCampaignPageProps } from './interface'

export function withEditCampaignPage(
  Component: React.FC<EditCampaignPageProps>
) {
  function WithEditCampaignPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const client = useClient()
    const alert = useAlert()
    const confirmModal = useModal({ modal: 'confirm' })
    const campaignId = Number(id)

    const { data: detail, isLoading } = useQuery(
      ['campaign-detail', campaignId],
      () => client?.promotionCampaignClient.getCampaignDetail(campaignId),
      {
        onError: () => navigate('/promotion-campaign'),
      }
    )

    const initialValues: CampaignFormType = detail
      ? {
          name: detail.name,
          type: detail.type,
          status: detail.status,
          startDate: undefined,
          endDate: undefined,
          wholeStoryDiscountType: detail.wholeStoryDiscountType,
          freeReadingType: detail.freeReadingType,
          bookData: detail.items || [],
          csvFile: undefined,
        }
      : {
          name: '',
          type: CampaignTypeEnum.PER_CHAPTER,
          status: CampaignStatusEnum.DRAFT,
          startDate: undefined,
          endDate: undefined,
          wholeStoryDiscountType: undefined,
          freeReadingType: undefined,
          bookData: [],
          csvFile: undefined,
        }

    const { mutateAsync: updateCampaign } = useMutation(
      (values: CampaignFormType) =>
        client!.promotionCampaignClient.updateCampaign({
          id: campaignId,
          ...values,
          status: CampaignStatusEnum.PENDING,
        }),
      {
        onSuccess: () => {
          alert.success('แก้ไขแคมเปญสำเร็จ')
          confirmModal.hide()
          navigate('/promotion-campaign')
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง')
          confirmModal.hide()
        },
      }
    )

    const { mutateAsync: saveDraft } = useMutation(
      (values: CampaignFormType) =>
        client!.promotionCampaignClient.updateCampaign({
          id: campaignId,
          ...values,
          status: CampaignStatusEnum.DRAFT,
        }),
      {
        onSuccess: () => {
          alert.success('บันทึกแบบร่างสำเร็จ')
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง')
        },
      }
    )

    async function handleSubmit(values: CampaignFormType) {
      await updateCampaign(values)
    }

    function handleLeave(values: CampaignFormType) {
      const canSaveDraft = !!values.name.trim()

      confirmModal.show({
        confirmButtonText: 'บันทึกแบบร่าง',
        closeButtonText: 'ออกจากหน้านี้',
        hideConfirmButton: !canSaveDraft,
        content: (
          <div className='w-[320px] mb-[20px]'>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium'>
              ออกจากการแก้ไขแคมเปญ
            </p>
            <div className='mt-5 text-sm font-light text-secondary text-center'>
              <p>หากคุณออกจากหน้านี้ ข้อมูลที่แก้ไขจะหายไป</p>
              {canSaveDraft && <p>คุณต้องการที่จะบันทึกแบบร่างไว้หรือไม่</p>}
            </div>
          </div>
        ),
        onConfirm: async () => {
          await saveDraft(values)
          confirmModal.hide()
          navigate(-1)
        },
        onClose: () => {
          confirmModal.hide()
          navigate(-1)
        },
      })
    }

    const componentProps: EditCampaignPageProps = {
      campaignType: detail?.type || CampaignTypeEnum.PER_CHAPTER,
      initialValues,
      isLoading,
      handleSubmit,
      handleLeave,
    }

    return <Component {...componentProps} />
  }

  return WithEditCampaignPage
}
